require([
	"dojo/_base/event",
	"dojo/on",
	"dojo/topic",
	"dijit/registry",
	"fontfaceobserver",
	"react",
	"react-dom/client",
	"underscore",
	"src/c8-platform/service/aggregateStoreService",
	"src/designSystem/dnd/utilities/manage-legacy-dnd",
	"csi",
	"csi/action",
	"csi/action/Controller",
	"csi/constants",
	"csi/comm/registry",
	"csi/comm/WebSocketChannel",
	"csi/config/preferences",
	"csi/configuration",
	"csi/datasetup/SectionData",
	"csi/def",
	"csi/def/Action",
	"csi/dndHTML5",
	"csi/heading/_base",
	"csi/html",
	"csi/keycloak/KeycloakAdapter",
	"csi/locale",
	"csi/middleware/goToDialog/goToDialogService",
	"csi/message",
	"csi/page",
	"csi/page/HeaderToolbar",
	"csi/page/HelpPane",
	"csi/page/Location",
	"csi/page/reactions",
	"csi/page/ReactRoot",
	"csi/resolver",
	"csi/router",
	"csi/session",
	"csi/sessionActions",
	"csi/sessionConstants",
	"csi/service/fileService",
	"csi/service/fontService",
	"csi/service/googleTagManagerService",
	"csi/service/iframeService",
	"csi/service/microsoftClarityService",
	"csi/service/notificationService",
	"csi/service/reflectionService",
	"csi/service/sessionService",
	"csi/service/rightPaneService",
	"csi/service/sessionIdleService",
	"csi/service/writerHoldService",
	"csi/service/writerService",
	"csi/state/datasetup",
	"csi/state/Cache",
	"csi/state/rootStore",
	"csi/store/UserNames",
	"csi/util",
	"csi/utility/htmlUtility",
	"csi/utility/routerUtility",
	"csi/view",
	"csi/widget/froalaTextEditor",
	"csi/widget/MessageDialog",
	"csi/widget/Tooltip",
	"csi/xml",

	"@fortawesome/fontawesome-free/js/all",
	"dijit/layout/StackContainer",
	"slick",
	"csi/page/Debug",
	"csi/page/Footer",
	"csi/page/RightPane",
	"csi/widget/CommentDialog",
	"csi/widget/Dialog",
	"csi/widget/Logo",
	"csi/widget/SplitPane"
], function(event, on, topic, registry, FontFaceObserver, React, ReactDom, _, {clearAggregateStoreIdCache, clearAggregateStoreResponsesOfDeletedUrl}, {registerDndCallback}, csi, csiAction, Controller,
	constants, {registerRequestPrerequisiteCallback}, WebSocketChannel, configPreferences, configuration, {getSections}, def, Action, dndHTML5, csiHeading, html, KeycloakAdapter, locale, {goToDialog}, message, page,
	HeaderToolbar, HelpPane, Location, {clearAggregateStoreResponsesForNode, listenForMouseUpDown}, ReactRoot, resolver, router, session, sessionActions, sessionConstants,
	fileService, fontService, googleTagManagerService, iframeService, microsoftClarityService, {initNotificationService}, reflectionService, {init}, rightPaneService, {sessionIdleInit}, {registerErrorCallback, submitHoldWriter}, {registerWriterCallback}, {dataSetupStore},
	{rootCache}, {browserStore, featureTogglesStore, rightPaneStore, rootStore, routerStore, sessionStore}, UserNames, util, {resetScrollbarSize}, {checkNavigation}, csiView, froalaTextEditor, MessageDialog, Tooltip, xml) {

	// Action: action handling changes to document location
	const _actionLocation = Object.assign(new Action(), {
		m_id: "Location",
		classController: Location
	});

	// Boolean: indicates if connected to server
	let _bConnected = true;

	// Boolean: indicates if unloading page
	let _bUnloading = false;

	function _changeConfiguration() {
		// for the rare case when Update Configuration finishes while a canvas is being saved, remove this class
		document.body.classList.remove("csi-canvas-progress");

		message.showDialog({
			message: locale.resource("UpdateConfiguration_Prompt"),
			title: locale.resource("UpdateConfiguration"),
			materialIcon: MessageDialog.ICON_QUESTION,
			buttons: [MessageDialog.BUTTON_YES + MessageDialog.BUTTON_PRIMARY, MessageDialog.BUTTON_NO],
			onResponse: (response) => {
				if (response == MessageDialog.BUTTON_YES) {
					location.reload();
				}
			}
		});
	}

	function _endConnection() {
		// summary: session has expired or server is down, so proceed to login page
		if (_bConnected) {
			_bConnected = false;

			if (!_bUnloading) {
				sessionActions.loginPage(true);
			}
		}
	}

	function _endDeferred() {
		// Add the special element to the body to help the client unit tests understand that the loading is complete.
		csi.markAsProgress(false);
	}

	function _startDeferred() {
		// Add the special element to the body to help the client unit tests understand that the loading is started.
		csi.markAsProgress(true);
	}

	function _createHeaderToolbar() {
		const user = sessionStore.currentUser;
		const view = csiView.newView(def.getNodeViewDef(user, "__HeaderToolbar__"), user, {
			classController: HeaderToolbar,
			m_typeView: "HeaderToolbar",
			cacheView: resolver.m_cache
		});

		// update state with context for current user
		sessionStore.userContext = view.getContext();

		view.placeAt("pageHeader");
		return view.startup();
	}

	function _handlePageEvents() {
		// stop non-modal actions such as inline editing when click outside any view
		on(html.pageCenter(), "click", () => csiAction.stopNonModalAction());

		on(window, "resize", _onResizeWindow);
		_onResizeWindow();

		// track whether mouse is up or down
		listenForMouseUpDown();

		sessionIdleInit();
	}

	async function _hashChange() {
		// summary: respond to change in browser location only if page is initialized and location is new
		const urlSession = sessionConstants.sessionUrl;

		if (_bConnected && !_bUnloading && session.captureLocation()) {
			if (urlSession && (urlSession != sessionConstants.sessionUrl)) {
				// if SessionURL has changed, then need to refresh the browser to get proper security
				location.reload();
			} else {
				// allow for listeners to intercept navigation and reject it if in the middle of something important
				const locationKey = session.getLocationKeyChanged();
				const isPageNavigation = (locationKey == session.LOCATION_URL) || (locationKey == session.LOCATION_REVISION_URL);
				const isTabNavigation = (locationKey == session.LOCATION_TAB);
				const allowed = await checkNavigation(isPageNavigation, isTabNavigation);

				if (allowed) {
					// stop any actions in progress before navigating to new location;
					// this handles the case where browser back button is used when in the middle of another action,
					// especially those that popup a dialog
					csiAction.stopAllActions();
					Tooltip.hide();

					// in case the user wants to swipe to go back on the new location, remove this
					document.body.parentElement.classList.remove(constants.TABLE_NO_SWIPE_TO_GO_BACK);

					// update state store with route (for React components)
					routerStore.captureLocation();

					// invoke action to navigate
					csiAction.invoke(null, null, _actionLocation);
				} else {
					// revert url hash change if deferred is cancelled
					session.restoreActualLocation();
				}
			}
		}
	}

	/**
	 * If an operation is in progress, then protect application from closing without a confirmation
	 */
	function _onBeforeUnload(e) {
		fileService.deleteFileStatusAttributes(browserStore.fileUploadMap);

		if (rootStore.isOperationInProgress) {
			// reference: https://developer.mozilla.org/en-US/docs/Web/API/WindowEventHandlers/onbeforeunload
			// Cancel the event
			e.preventDefault();

			// Chrome requires returnValue to be set
			e.returnValue = '';
		} else {
			// track that page is unloading
			_bUnloading = true;

			// publish any held changes from inline edits
			submitHoldWriter(false, true);

			// busy loop for 500ms to allow time for any operations to publish - a timeout will not work to stop page from unloading
			const time = new Date().getTime();
			let time2 = time;

			while (time2 - time < 500) {
				time2 = new Date().getTime();
			}
		}
	}

	/**
	 * Handle keyup event, for the case where special logic reply on this event
	 * @param {Event} e
	 */
	function _onKeyRelease(e) {
		csiAction.onKeyRelease(e);
	}

	/**
	 * Handle page key events
	 * @param {Event} e
	 */
	function _onKeyPress(e) {
		var params = {
			_altKey: e.altKey,
			_ctrlKey: e.ctrlKey || e.metaKey,
			_shiftKey: e.shiftKey
		};

		var w = registry.getEnclosingWidget(e.target);
		if (w && (w.focusNode == e.target) && (w.csiIgnoreKeyPress !== false)) {
			//return if the focus is on a widget
			return;
		}

		try {
			if (csiAction.onKeyPress(e, params)) {
				// active action/view processed keypress
				event.stop(e);
			} else if (params._ctrlKey && (e.keyCode == 81 || e.charCode == 113 || e.charCode == 17) && !(params._altKey || params._shiftKey)) {
				// Ctrl+Q will show/hide context help, Check altKey/shift press to fix German key defect (Ctrl+Alt+Q) = @  in German
				if (!sessionConstants.isPortalUser) {
					HelpPane.toggle();
				}
				event.stop(e);
			}
		} catch (ex) {
			console.warn(ex);
		}
	}

	/**
	 * React to resizing of page content
	 */
	const _onResizeContent = _.throttle(resizePage, 250);

	/**
	 * React to window resize event
	 */
	const _onResizeWindow = _.throttle(() => {
		// track browser zoom and the proper widths for scrollbars
		resetScrollbarSize();
		html.setCSSVariable(document.body, "DevicePixelRatio", window.devicePixelRatio);
		_onResizeContent();
	}, 250, {leading: false});

	/**
	 * React to changes to nodes due to an action
	 * @param {Response} res
	 */
	function _onTopicActionResponse(res) {
		const {nonObservableCache} = sessionStore;
		const {locationUrl, locationRevisionUrl} = routerStore;
		const observingLocation = router.isObservingLocation();
		const nodes = res?.getNodes?.();
		const deletedUrls = res?.getDeletedUrls?.();
		const urlMaster = xml.isValidURL(locationRevisionUrl) ? locationUrl : null;

		nodes?.forEach((node) => {
			const url = node.$URL;

			// update caches with any new versions of the nodes if nodes in response, then update cache with all nodes in breadcrumb trail -
			// also cache any new revision nodes if currently viewing a master
			if (rootCache.get(url) || (urlMaster && (urlMaster === node.Master))) {
				rootCache.add(node);
			} else if (nonObservableCache.get(url)) {
				// this cache can have nodes the session cache does not through csi/resolver, so need to make sure it stays updated as well
				nonObservableCache.add(node);
			}

			// if location node or revision node changed, then force redraw of breadcrumb (until all state managed by MobX)
			if (!observingLocation && ((url === locationUrl) || (url === locationRevisionUrl))) {
				routerStore.updateBreadcrumb();
			}

			// update aggregate store cache based on change to node
			clearAggregateStoreResponsesForNode(node);
		});

		// clear aggregate stores of any deleted urls
		deletedUrls?.forEach(clearAggregateStoreResponsesOfDeletedUrl);
	}

	/**
	 * Force refresh to host view
	 */
	function refreshHostView() {
		// wait so we give time for action controller to finish before recreating all views
		// TODO: when called, should layoutStore.viewedSet be reset to only the selection?
		// TODO: try using Tracking.addJob instead?
		return util.wait(100).then(routerStore.updateHostView());
	}

	/**
	 * Force resize for all views
	 */
	function resizePage() {
		topic.publish("/csi/window/resize");
	}

	/**
	 * Initialize and load resources for page
	 */
	async function loadResources() {
		// ensure that fonts are loaded - especially Material Icons which causes header to stretch if not loaded
		const promises = [
			new FontFaceObserver("Material Icons").load(),
			new FontFaceObserver("Centric8", {weight: 400}).load(),
			new FontFaceObserver("Centric8", {weight: 600}).load(),
			new FontFaceObserver("Centric8", {weight: 700}).load(),
			froalaTextEditor.loadLanguageResources(),
		];

		WebSocketChannel.initialize();

		if (WebSocketChannel.isEnabled()) {
			WebSocketChannel.reconnect();
			sessionStore.websocketsEnabled = true;
		}

		// store locale for user in local storage - do so here in case user enters page from SSO login rather than our login page;
		// we also do so here only for home.html and not for print or fragment pages which are used to print in different locales
		locale.storeLocale(sessionConstants.userLocale);

		// enable special styling per user type - this must be done before DOM parsing can begin
		if (sessionConstants.isLocalUser) {
			document.body.classList.add(sessionConstants.userType);
		}

		return Promise.all(promises);
	}

	/**
	 * Setup right pane components
	 */
	async function addRightPaneComponents() {
		return Promise.all([
			rightPaneService.getCommentsComponent(),
			rightPaneService.get3DViewerSnapshot(),
			rightPaneService.get3DModelComponents(),
			rightPaneService.get3DLighting(),
			rightPaneService.getProcessOnInstanceComponent(),
			rightPaneService.getFashionInspirationComponent(),
			rightPaneService.getChangeLogComponent(),
			rightPaneService.getChangeLogSlimComponent(),
			rightPaneService.getIngredientContributorComponent(),
			rightPaneService.getComplianceAnalysisRightComponentFLDS(),
			rightPaneService.getComplianceAnalysisRightComponentBaseProduct(),
			rightPaneService.getSlackComponent(),
			rightPaneService.getMSTeamsComponent(),
		]).then((results) => {
			results.forEach((component) => {
				if (component) {
					rightPaneStore.add(component);
				}
			});
		});
	}

	function handleRedirect() {
		let redirectUrl = session.getLocationValue('RedirectUrl');
		if (redirectUrl) {
			window.location.assign(redirectUrl);
		}
	}

	/**
	 * Kick the user out of a dead session
	 */
	function onSessionDestroyed() {
		sessionActions.loginPage(true);
	}

	/**
	 * Open error dialog when get error from publishing held operations
	 * @param {Error} err
	 */
	function onWriterHoldError(err) {
		message.errorDialog({
			message: err.message || err,
			onResponse: () => {
				topic.publish("/csi/action/error");
			}
		});
	}

	// handle dropped connections
	topic.subscribe("/csi/servermessage/HTTP_SESSION_DESTROYED", onSessionDestroyed);
	topic.subscribe("/csi/servermessage/C8_SESSION_DESTROYED", onSessionDestroyed);
	topic.subscribe("/csi/websocketchannel/RETRY_COUNT_EXCEEDED", onSessionDestroyed);

	init()
		.then(() => reflectionService.init())
		.then(() => def.init())

		.then(() => handleRedirect())
		.then(loadResources)
		.then(UserNames.load)
		.then(addRightPaneComponents)
		.then(csi.loadHtml)
		.then(async () => {
			// initialize google tag manager
			googleTagManagerService.init();

			// initialize microsoft clarity
			microsoftClarityService.init();

			// initialize the sections
			dataSetupStore.sections = getSections();

			// setup routing and other callbacks and capture initial page location
			session.captureLocation();
			_handlePageEvents();
			dndHTML5.init();
			iframeService.connectToIframeParent();

			router.reserveLocationKeys();
			page.setBreadcrumbChangeHandler(router.resolveCurrentLocation);
			page.setRefreshHandler(refreshHostView);
			page.setResizeHandler(resizePage);
			registerErrorCallback(onWriterHoldError);
			registerWriterCallback(_onTopicActionResponse);
			registerDndCallback("init", dndHTML5.init);
			registerDndCallback("reset", dndHTML5.reset);

			// add observer for all AJAX requests; if any ajax requests are going out,
			// then need to make sure any held changes are published first
			registerRequestPrerequisiteCallback(submitHoldWriter);

			// handle page unload event
			window.onbeforeunload = _onBeforeUnload;

			// add events that page is interested in
			topic.subscribe("/csi/action/error", () => page.refreshViews());
			topic.subscribe("/csi/configuration/change", _changeConfiguration);
			topic.subscribe("/csi/dfd/start", _startDeferred);
			topic.subscribe("/csi/dfd/end", _endDeferred);
			topic.subscribe("/csi/xhr/drop", _endConnection);

			if (!sessionConstants.isPortalUser) {
				// add events that page is interested in
				// use keydown instead of keypressed as chrome don't support keypressed for arrow keys
				on(document, "keydown", _onKeyPress);
				on(document, "keyup", _onKeyRelease);
			}

			const container = document.getElementById("reactroot");
			const root = ReactDom.createRoot(container);
			root.render(React.createElement(ReactRoot.default));

			// check configuration status at regular intervals
			configuration.refreshStatus();

			// load font families and weights from reflection nodes
			fontService.loadFontsFromReflection();

			// register writer callback function
			registerWriterCallback(() => clearAggregateStoreIdCache());

			// extension point for page initialization
			topic.publish("/csi/page/init");

			// initialize notifications
			initNotificationService();

			// initialize large editable icons if configured
			if (featureTogglesStore.UX_EDITABLE_LARGE_ICONS) {
				csiHeading.useLargeIcons();
			}

			// register help pane and render header view
			await _createHeaderToolbar();

			// destroy splash screen
			const splash = document.querySelector("#splash");
			if (splash) {
				html.orphan(splash);
			}

			// navigate to first page
			window.onhashchange = _hashChange;
			_hashChange();

			// resolve circular dependency between controller and goToDialogService
			Controller.goToDialog = goToDialog;
		})
		.catch((err) => {
			console.error("Page load error=", err);

			if (locale.getLocale()) {
				alert(locale.resource("SERVER_UNAVAILABLE"));
			}

			if (KeycloakAdapter.isKeycloakEnabled()) {
				KeycloakAdapter.logoutOnError();
			} else {
				_endConnection();
			}
		});
});
